import Image from "next/image";
import { TrackWithProxy } from "@/lib/types";
import PositionBadge from "./PositionBadge";
import AudioPreview from "./AudioPreview";

interface TrackRowProps {
  track: TrackWithProxy;
}

export default function TrackRow({ track }: TrackRowProps) {
  const isTop3 = track.position <= 3;
  const isTop10 = track.position <= 10;

  return (
    <div
      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-200 sm:gap-4 sm:px-4 sm:py-3 ${
        isTop3
          ? "bg-gradient-to-r from-violet-500/10 to-transparent hover:from-violet-500/20"
          : "hover:bg-white/[0.04]"
      }`}
    >
      {/* Position */}
      <div className="flex w-8 flex-shrink-0 flex-col items-center gap-0.5 sm:w-12">
        <span
          className={`text-base font-bold sm:text-lg ${
            isTop3
              ? "bg-gradient-to-b from-violet-300 to-violet-500 bg-clip-text text-transparent"
              : isTop10
                ? "text-white/90"
                : "text-white/50"
          }`}
        >
          {track.position}
        </span>
        <PositionBadge
          position={track.position}
          prevPosition={track.prev_position}
        />
      </div>

      {/* Cover */}
      <div className="relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg shadow-lg sm:h-12 sm:w-12">
        {track.cover_url ? (
          <Image
            src={track.cover_url}
            alt={`${track.title} - ${track.artist}`}
            fill
            sizes="48px"
            className="object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-white/10 to-white/5">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-white/30"
            >
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </div>
        )}
      </div>

      {/* Track Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p
            className={`truncate text-sm font-semibold sm:text-base ${
              isTop3 ? "text-white" : "text-white/90"
            }`}
          >
            {track.title}
          </p>
          {track.explicit && (
            <span className="flex-shrink-0 rounded bg-white/10 px-1 py-0.5 text-[9px] font-bold leading-none text-white/40">
              E
            </span>
          )}
        </div>
        <p className="truncate text-xs text-white/50 sm:text-sm">
          {track.artist}
        </p>
      </div>

      {/* Stats - Hidden on mobile */}
      <div className="hidden items-center gap-4 lg:flex">
        <div className="flex w-16 flex-col items-center">
          <span className="text-[10px] uppercase tracking-wider text-white/30">
            Пик
          </span>
          <span className="text-xs font-medium text-white/60">
            #{track.peak}
          </span>
        </div>
        <div className="flex w-16 flex-col items-center">
          <span className="text-[10px] uppercase tracking-wider text-white/30">
            Дней
          </span>
          <span className="text-xs font-medium text-white/60">
            {track.streak}
          </span>
        </div>
      </div>

      {/* Audio Preview */}
      <div className="flex-shrink-0">
        <AudioPreview previewUrl={track.proxyUrl} />
      </div>
    </div>
  );
}
